import { useEffect, useState } from 'preact/hooks';
import { api, type Dataset } from '@/lib/teaching/teacher-api';
import { useAssessmentId, invalidateDataset } from '@/lib/teaching/state';
import { errText, waitForJob, markText, type Decision, type DecisionBody, type Job, type Queue, type Recheck, type ScansView } from '@/lib/teaching/slice2';
import { Card } from '@/components/teaching/ui';
import { Btn, ErrorLine, Field, inputCls, val } from '@/components/teaching/form';
import { useAiReady } from '@/lib/teaching/ai-ready';
import { NoAiNote, Pill, SetupPage, Note, textareaCls, selectCls } from '@/components/teaching/setup';

// Moderation Log (SOP §14): every decision is permanent; a retroactive one
// lists the results to recheck, which close only by explicit review.

const RECHECK: Record<Decision['recheck_status'], { label: string; bg: string }> = {
  not_required: { label: 'NOT REQUIRED', bg: '#e2e3e6' },
  pending: { label: 'RECHECK PENDING', bg: '#fde9b8' },
  complete: { label: 'COMPLETE', bg: '#d3f0d9' },
};

interface Ctx { a: string; ds: Dataset; scans: ScansView; queue: Queue; reload: () => Promise<void> }

export function Moderation() {
  const a = useAssessmentId();
  const [log, setLog] = useState<Decision[] | null>(null);
  const [ctx, setCtx] = useState<Ctx | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  async function load() {
    if (!a) return;
    const [m, ds, scans, queue] = await Promise.all([
      api<{ decisions: Decision[] }>('GET', `/api/t/assessments/${a}/moderation`), api<Dataset>('GET', `/api/t/assessments/${a}/dataset`),
      api<ScansView>('GET', `/api/t/assessments/${a}/scans`), api<Queue>('GET', `/api/t/assessments/${a}/queue`),
    ]);
    setLog(m.decisions);
    setCtx({ a, ds, scans, queue, reload: async () => { invalidateDataset(a); await load(); } });
  }
  useEffect(() => { setLog(null); void load().catch((e) => setErr(errText(e))); }, [a]);

  const itemLabel = (id: string | null) => (id ? ctx?.ds.items.find((i) => i.id === id)?.label ?? '?' : 'Whole test');
  const scopeText = (d: Decision) => d.scope === 'assessment' ? 'Whole assessment'
    : d.scope === 'class' ? `Class ${ctx?.scans.tracker.find((t) => t.class_id === d.scope_class_id)?.name ?? ''}`
      : `Student ${ctx?.ds.students.find((s) => s.student_id === d.scope_student_id)?.name ?? ''}`;
  const fromText = (d: Decision) => (d.applies_from_paper_id ? ctx?.scans.papers.find((p) => p.id === d.applies_from_paper_id)?.scan_number ?? '?' : '-');

  return (
    <SetupPage a={a} title="Moderation" actions={<Btn kind="primary" onClick={() => setAdding(!adding)}>{adding ? 'Close' : 'New decision'}</Btn>}>
      <ErrorLine error={err} />
      {!log && !err && <div class="text-[13px] text-[var(--color-text-muted)]">Loading…</div>}
      {ctx && log && (
        <>
          {adding && <NewDecision ctx={ctx} onDone={async () => { setAdding(false); await ctx.reload(); }} />}
          <Card class="!p-0 overflow-hidden">
            <div class="overflow-x-auto">
              <table class="w-full text-[12.5px] border-collapse min-w-[860px]">
                <thead>
                  <tr class="text-left text-[11px] uppercase tracking-wider text-[var(--color-text-faint)] bg-[var(--color-elevated)]">
                    <th class="p-2">ID</th><th class="p-2">Date</th><th class="p-2">Item</th><th class="p-2">Original interpretation</th><th class="p-2">Teacher decision</th>
                    <th class="p-2">Applies to</th><th class="p-2">From</th><th class="p-2">Retro</th><th class="p-2">Recheck</th><th class="p-2">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {log.map((d) => (
                    <tr key={d.id} class="border-t border-[var(--color-border)] align-top cursor-pointer hover:bg-[var(--color-elevated)]" data-decision={d.code} onClick={() => setOpen(open === d.id ? null : d.id)}>
                      <td class="p-2 font-semibold text-[var(--color-accent)] whitespace-nowrap">{d.code}</td>
                      <td class="p-2 whitespace-nowrap text-[var(--color-text)]">{d.decided_on}</td>
                      <td class="p-2 text-[var(--color-text)]">{itemLabel(d.item_id)}</td>
                      <td class="p-2 text-[var(--color-text)] max-w-[220px]">{d.original_interpretation}</td>
                      <td class="p-2 text-[var(--color-text)] max-w-[220px]">{d.teacher_decision}</td>
                      <td class="p-2 text-[var(--color-text)]">{scopeText(d)}</td>
                      <td class="p-2 text-[var(--color-text)]">{fromText(d)}</td>
                      <td class="p-2 text-[var(--color-text)]">{d.retro ? 'Yes' : 'No'}</td>
                      <td class="p-2"><Pill {...RECHECK[d.recheck_status]} />{d.retro && <div class="text-[11px] text-[var(--color-text-faint)] mt-0.5">{d.rechecks.filter((r) => r.status !== 'pending').length}/{d.rechecks.length} done</div>}</td>
                      <td class="p-2 text-[var(--color-text-muted)] max-w-[180px]">{d.notes || '-'}</td>
                    </tr>
                  ))}
                  {log.length === 0 && <tr><td colSpan={10} class="p-4 text-[13px] text-[var(--color-text-muted)]">No moderation decisions yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
          {log.filter((d) => d.retro && d.rechecks.length > 0 && (open === d.id || d.recheck_status === 'pending')).map((d) => <Rechecks key={d.id} ctx={ctx} d={d} />)}
        </>
      )}
    </SetupPage>
  );
}

function NewDecision({ ctx, onDone }: { ctx: Ctx; onDone: () => Promise<void> }) {
  const [b, setB] = useState<DecisionBody>({ item_id: null, original_interpretation: '', teacher_decision: '', scope: 'assessment', retro: true, notes: '', decided_on: new Date().toISOString().slice(0, 10) });
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const set = (p: Partial<DecisionBody>) => setB({ ...b, ...p });

  async function save() {
    setErr(null); setSaving(true);
    const body: DecisionBody = { ...b, original_interpretation: b.original_interpretation.trim(), teacher_decision: b.teacher_decision.trim() };
    if (body.scope !== 'student') delete body.scope_student_id;
    if (body.scope !== 'class') delete body.scope_class_id;
    if (!body.applies_from_paper_id) delete body.applies_from_paper_id;
    try { await api('POST', `/api/t/assessments/${ctx.a}/moderation`, body); await onDone(); } catch (e) { setErr(errText(e)); setSaving(false); }
  }

  return (
    <Card title="New moderation decision" sub="Recorded permanently. AI marks still waiting for your check are set aside until those papers are re-marked.">
      <div class="grid gap-3 md:grid-cols-2">
        <Field label="Item">
          <select class={selectCls} value={b.item_id ?? ''} onChange={(e) => set({ item_id: val(e) || null })}>
            <option value="">Whole test</option>
            {ctx.ds.items.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
          </select>
        </Field>
        <Field label="Date"><input type="date" class={inputCls} value={b.decided_on} onInput={(e) => set({ decided_on: val(e) })} /></Field>
        <Field label="Original interpretation"><textarea rows={2} class={textareaCls} value={b.original_interpretation} onInput={(e) => set({ original_interpretation: val(e) })} /></Field>
        <Field label="Teacher decision"><textarea rows={2} class={textareaCls} value={b.teacher_decision} onInput={(e) => set({ teacher_decision: val(e) })} /></Field>
        <Field label="Applies to">
          <select class={selectCls} value={b.scope} onChange={(e) => set({ scope: val(e) as DecisionBody['scope'] })}>
            <option value="assessment">Whole assessment</option><option value="class">One class</option><option value="student">One student</option>
          </select>
        </Field>
        {b.scope === 'class' && (
          <Field label="Class">
            <select class={selectCls} value={b.scope_class_id ?? ''} onChange={(e) => set({ scope_class_id: val(e) })}>
              <option value="">Pick…</option>{ctx.scans.tracker.map((t) => <option key={t.class_id} value={t.class_id}>{t.name}</option>)}
            </select>
          </Field>
        )}
        {b.scope === 'student' && (
          <Field label="Student">
            <select class={selectCls} value={b.scope_student_id ?? ''} onChange={(e) => set({ scope_student_id: val(e) })}>
              <option value="">Pick…</option>{ctx.ds.students.map((s) => <option key={s.student_id} value={s.student_id}>{s.name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Applies from paper (optional)">
          <select class={selectCls} value={b.applies_from_paper_id ?? ''} onChange={(e) => set({ applies_from_paper_id: val(e) || undefined })}>
            <option value="">-</option>{ctx.scans.papers.filter((p) => p.batch_id).map((p) => <option key={p.id} value={p.id}>{p.scan_number}{p.student_name ? ` · ${p.student_name}` : ''}</option>)}
          </select>
        </Field>
        <Field label="Notes"><input class={inputCls} value={b.notes} onInput={(e) => set({ notes: val(e) })} /></Field>
      </div>
      <label class="mt-3 text-[12.5px] text-[var(--color-text)] flex items-center gap-1.5"><input type="checkbox" checked={b.retro} onChange={() => set({ retro: !b.retro })} />Retroactive recheck required</label>
      <ErrorLine error={err} />
      <div class="mt-3"><Btn kind="primary" disabled={saving || !b.original_interpretation.trim() || !b.teacher_decision.trim()} onClick={save}>Record decision</Btn></div>
    </Card>
  );
}

function Rechecks({ ctx, d }: { ctx: Ctx; d: Decision }) {
  const [job, setJob] = useState<Job | null>(null);
  const [manual, setManual] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const pending = d.rechecks.filter((r) => r.status === 'pending');
  const running = job && (job.status === 'queued' || job.status === 'running');
  const ai = useAiReady();

  async function run() {
    setErr(null);
    try {
      const r = await api<{ job: Job | null; manual: string[] }>('POST', `/api/t/assessments/${ctx.a}/moderation/${d.id}/recheck`);
      setManual(r.manual);
      if (r.job) {
        setJob(r.job);
        const done = await waitForJob(r.job.id, setJob);
        if (done.status === 'failed') setErr(`Recheck failed: ${done.error ?? 'unknown error'}`);
        setJob(null);
      }
      await ctx.reload();
    } catch (e) { setErr(errText(e)); setJob(null); }
  }

  return (
    <Card title={`${d.code} recheck · ${d.rechecks.length - pending.length} of ${d.rechecks.length} resolved`} sub={d.teacher_decision}
      right={pending.length > 0 && <Btn kind="primary" disabled={!!running} onClick={run}>{running ? 'Re-marking…' : 'Run recheck'}</Btn>}>
      {ai === false && pending.length > 0 && <div class="mb-2"><NoAiNote /></div>}
      {manual.length > 0 && <div class="mb-2"><Note tone="warn">{manual.length} result{manual.length === 1 ? ' has' : 's have'} no scanned paper; review {manual.length === 1 ? 'it' : 'them'} by hand below.</Note></div>}
      <ErrorLine error={err} />
      <div class="flex flex-col gap-2">
        {d.rechecks.map((r) => <RecheckRow key={r.id + r.status} ctx={ctx} d={d} r={r} />)}
      </div>
    </Card>
  );
}

function RecheckRow({ ctx, d, r }: { ctx: Ctx; d: Decision; r: Recheck }) {
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState(r.mark === null ? '' : String(r.mark));
  const [err, setErr] = useState<string | null>(null);
  const max = ctx.ds.items.find((i) => i.id === r.item_id)?.max_mark;
  const proposal = ctx.queue.papers.flatMap((p) => p.cells.map((c) => ({ p, c })))
    .find(({ p, c }) => p.student_id === r.student_id && c.item_id === r.item_id && c.proposal?.status === 'pending' && c.proposal.decision_id === d.id && !c.proposal.stale)?.c.proposal;
  const rev = ctx.ds.students.find((s) => s.student_id === r.student_id)?.revision;

  async function resolve(body: Record<string, unknown>) {
    setErr(null);
    try { await api('POST', `/api/t/assessments/${ctx.a}/moderation/${d.id}/rechecks/${r.id}/resolve`, { ...body, expectedRowRevision: rev }); await ctx.reload(); }
    catch (e) { setErr(errText(e)); }
  }
  function saveEdit() {
    const s = text.trim().toLowerCase();
    const values = s === 'b' ? { mark: 0, blank: true } : s === 'u' ? { mark: null, unavailableReason: 'unreadable' } : s === 'm' ? { mark: null, unavailableReason: 'page missing' } : /^\d+$/.test(s) ? { mark: Number(s) } : null;
    if (!values) { setErr('Type a mark, or b blank, u unreadable, m page missing'); return; }
    void resolve({ action: 'edit', values });
  }

  return (
    <div class="rounded-xl border border-[var(--color-border)] p-2.5 flex flex-col gap-1.5" data-recheck={`${r.student_name} ${r.item_label}`}>
      <div class="flex flex-wrap items-center gap-2 text-[12.5px]">
        <b class="text-[var(--color-text)]">{r.student_name}</b><span class="text-[var(--color-text-muted)]">{r.item_label}</span>
        <span class="tabular-nums text-[var(--color-text)]">current {r.mark ?? '-'}{max !== undefined ? `/${max}` : ''}</span>
        <Pill label={r.status.toUpperCase()} bg={r.status === 'pending' ? '#fde9b8' : '#d3f0d9'} />
        {proposal && <span class="text-[12px] text-[var(--color-text)]">AI re-mark: <b>{markText(proposal)}</b>{proposal.confidence ? ` (${proposal.confidence.toUpperCase()})` : ''}</span>}
      </div>
      {r.status === 'pending' && (
        <div class="flex flex-wrap gap-2 items-center">
          <Btn onClick={() => resolve({ action: 'unchanged' })}>Confirmed unchanged</Btn>
          {proposal && <Btn kind="primary" onClick={() => resolve({ action: 'approve', proposalId: proposal.id })}>Approve re-mark</Btn>}
          <Btn onClick={() => setEdit(!edit)}>{edit ? 'Cancel' : 'Edit mark'}</Btn>
          {edit && <><input aria-label={`New mark ${r.student_name} ${r.item_label}`} class={inputCls + ' w-20'} value={text} onInput={(e) => setText(val(e))} /><Btn kind="primary" onClick={saveEdit}>Save</Btn></>}
        </div>
      )}
      <ErrorLine error={err} />
    </div>
  );
}
